import { format } from "date-fns";
import { TFunction } from "i18next";

import { AssignmentType, IRegMenReportFilters } from "~/entity/RegMenReports";

export interface OptionItem {
  label: string;
  value: string | number;
}

export interface PeriodFilterValues {
  selectFrom: OptionItem;
  filterValues: OptionItem[];
  regionalManager: OptionItem | null;
  assignmentType: OptionItem | null;
  actualOn: Date | null;
  quarter: OptionItem;
  year: OptionItem;
}

export type FilterCategory = "dealer" | "city" | "FO" | "subject" | "country";

const CATEGORY_LABEL_KEYS: Record<FilterCategory, string> = {
  dealer: "regMen.categoryDealer",
  city: "regMen.city",
  FO: "regMen.FO",
  subject: "regMen.subject",
  country: "regMen.country",
};

export const getCategoryOptions = (t: TFunction): OptionItem[] =>
  (Object.keys(CATEGORY_LABEL_KEYS) as FilterCategory[]).map((category) => ({
    value: category,
    label: t(CATEGORY_LABEL_KEYS[category]),
  }));

export const getYearOptions = (): OptionItem[] => {
  const currentYear = new Date().getFullYear();
  const result: OptionItem[] = [];
  for (let year = currentYear; year > currentYear - 10; year--) {
    result.push({ label: String(year), value: year });
  }
  return result;
};

export const getQuarterOptions = (): OptionItem[] =>
  [1, 2, 3, 4].map((quarter) => ({ label: String(quarter), value: quarter }));

export const getAssignmentOptions = (t: TFunction): OptionItem[] => [
  { value: "permanent", label: t("regMen.permanent") },
  { value: "temporary", label: t("regMen.temporary") },
];

export const getDefaultValues = (t: TFunction): PeriodFilterValues => ({
  selectFrom: getCategoryOptions(t)[0],
  filterValues: [],
  regionalManager: null,
  assignmentType: null,
  actualOn: null,
  quarter: getQuarterOptions()[0],
  year: getYearOptions()[0],
});

/** Собирает фильтры REST-отчёта из значений формы. */
export const parsePeriodFilters = (
  data: PeriodFilterValues,
): IRegMenReportFilters => {
  const filters: IRegMenReportFilters = {
    quarter: Number(data.quarter?.value),
    year: Number(data.year?.value),
  };

  const selected = data.filterValues ?? [];
  if (selected.length > 0) {
    const stringValues = selected.map((item) => String(item.value));
    switch (data.selectFrom?.value as FilterCategory) {
      case "city":
        filters.dealerGroup = { cities: stringValues };
        break;
      case "FO":
        filters.dealerGroup = { FOs: stringValues };
        break;
      case "subject":
        filters.dealerGroup = { subjects: stringValues };
        break;
      case "country":
        filters.dealerGroup = { countries: stringValues };
        break;
      default:
        filters.dealerGroup = {
          ids: selected.map((item) => Number(item.value)),
        };
    }
  }

  if (data.regionalManager?.value) {
    filters.regionalManagerId = Number(data.regionalManager.value);
  }
  if (data.assignmentType?.value) {
    filters.assignmentType = data.assignmentType.value as AssignmentType;
  }
  if (data.actualOn) {
    filters.actualOn = format(new Date(data.actualOn), "yyyy-MM-dd");
  }

  return filters;
};
